import React, { useCallback, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { AuthContext, IntlContext } from "context";
import { InfiniteListContext } from "context/InfiniteList/Context";
import { DebouncedKeywordSearchForm, InfiniteListPage, KeywordNoResults } from "components/ListPage";
import { InfiniteListArea } from "components/List";
import ComponentList from "./ComponentList";
import AdCardSkeleton from "./AdCardSkeleton";
import FiltersBar from "./FiltersBar";
import SelectSort, { SortOption } from "app/Booking/List/SelectSort";
import useQueryString from "helpers/useQueryString";
import { fetchOurAds } from "hooks/ad";
import { AdInterface } from "types/ad";
import Seo from "components/Seo";
import {
    buildResetInlineAdFilterSearchParams,
    buildSearchParams,
    getSingleQueryValue,
    hasActiveInlineAdFilters,
} from "./searchParams";

import { Card } from "components/ui/card";
import { PenBox } from "lucide-react";
import { ROUTES } from "routes";

const DEFAULT_FETCHER_PARAMS = {
    order: "created_at",
    direction: "desc",
    per_page: 24,
} as const;

const AdsGridSkeleton: React.FC<{ count: number }> = ({ count }) => (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: count }).map((_, i) => (
            <AdCardSkeleton key={i} />
        ))}
    </div>
);

const AdsList: React.FC = () => {
    const intl = useContext(IntlContext);
    const auth = useContext(AuthContext);
    const currentRole = auth?.currentRole;
    const isLoggedIn = Boolean(auth?.isLoggedIn);
    const { pager, activeFilters, changeFetcherParams, requestedPerPage } = useContext(InfiniteListContext);
    const { values: queryValues, setAll } = useQueryString();

    const currentKeyword = getSingleQueryValue(queryValues.filter_keyword).trim();
    const selectedCity = getSingleQueryValue(queryValues.filter_city ?? activeFilters?.filter_city).trim();
    const selectedRadius = getSingleQueryValue(queryValues.filter_range ?? activeFilters?.filter_range).trim();
    const hasSelectedCity = selectedCity !== "";
    const hasKeyword = currentKeyword !== "";
    const hasInlineAdFilters = hasActiveInlineAdFilters({ ...activeFilters, ...queryValues });
    const providerTravelRange = auth?.account?.resources?.provider?.[0]?.travel_range;
    const searchPlaceholder = String(intl.formatMessage("ad.list.search_placeholder"));

    const sortOptions = useMemo<SortOption[]>(
        () => [
            {
                field: "created_at",
                order: "desc" as const,
                label: intl.formatMessage("ad.order.date.desc"),
            },
            {
                field: "created_at",
                order: "asc" as const,
                label: intl.formatMessage("ad.order.date.asc"),
            },
        ],
        [intl],
    );

    const handleKeywordReset = useCallback(() => {
        const params = buildSearchParams({
            activeFilters: activeFilters as HashMap<unknown>,
            pager,
        });

        changeFetcherParams(params);
        setAll(params);
    }, [changeFetcherParams, activeFilters, pager, setAll]);

    const handleKeywordSubmit = useCallback(
        (keyword: string) => {
            const params = buildSearchParams({
                activeFilters: activeFilters as HashMap<unknown>,
                keyword,
                pager,
            });

            changeFetcherParams(params);
            setAll(params);
        },
        [changeFetcherParams, activeFilters, pager, setAll],
    );

    const handleFiltersReset = useCallback(() => {
        const params = buildResetInlineAdFilterSearchParams({
            currentQueryValues: queryValues,
            pager,
        });

        changeFetcherParams(params);
        setAll(params);
    }, [changeFetcherParams, pager, queryValues, setAll]);

    const inlineNoResults = (hasKeyword || hasInlineAdFilters) && (
        <KeywordNoResults
            message={String(
                hasKeyword
                    ? intl.formatMessage("ad.list.search_no_results", { keyword: currentKeyword })
                    : intl.formatMessage("ad.list.filters.no_results"),
            )}
            resetLabel={String(intl.formatMessage(hasKeyword ? "ad.list.search_reset" : "ad.list.filters.reset"))}
            onReset={hasKeyword ? handleKeywordReset : handleFiltersReset}
        />
    );

    return (
        <React.Fragment>
            <Seo
                title={intl.formatMessage("ad.seo.list.title")}
                description={intl.formatMessage("ad.seo.list.description")}
                canonical={ROUTES.ADS}
                ogType="website"
            />
            <Card className="rounded-3xl p-4 sm:p-6">
                <div className="flex flex-col gap-3">
                    <DebouncedKeywordSearchForm
                        currentKeyword={currentKeyword}
                        placeholder={searchPlaceholder}
                        onDebouncedChange={handleKeywordSubmit}
                    />
                    <FiltersBar />
                </div>
            </Card>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-muted-foreground">
                    {intl.formatMessage("ad.list.results_count", { count: pager.total })}
                    {currentRole === "provider" && !hasSelectedCity && providerTravelRange != null && (
                        <>
                            &nbsp;
                            {intl.formatMessage("ad.list.results_travel_range", {
                                range: providerTravelRange,
                                city: auth?.account?.user?.city ?? "",
                                postcode: auth?.account?.user?.postcode ?? "",
                            })}
                            <Link
                                to={`${ROUTES.ACCOUNT_PROFILE}#travel-range`}
                                className="ml-1 inline-flex align-middle"
                                aria-label={String(intl.formatMessage("ad.list.go_to_edit_travel_range"))}
                                title={String(intl.formatMessage("ad.list.edit_travel_range"))}
                            >
                                <PenBox className="h-3 w-3 text-muted-foreground" />
                            </Link>
                        </>
                    )}
                    {hasSelectedCity && selectedRadius !== "" && (
                        <>
                            &nbsp;
                            {intl.formatMessage("ad.list.results_filter_radius", {
                                range: selectedRadius,
                                city: selectedCity,
                            })}
                        </>
                    )}
                </div>
                <SelectSort
                    sortOptions={sortOptions}
                    withIconSort={true}
                    pager={pager}
                    activeFilters={activeFilters as HashMap<unknown>}
                    onChangeFetcherParams={changeFetcherParams as (params: HashMap<unknown>) => void}
                />
            </div>

            <div className="mt-6 scroll-mt-6">
                <InfiniteListArea
                    skeleton={<AdsGridSkeleton count={requestedPerPage} />}
                    emptyContent={inlineNoResults}
                    labels={{
                        errorTitle: intl.formatMessage("ad.list.error_title"),
                        errorDescription: intl.formatMessage("ad.list.error_description"),
                        retry: intl.formatMessage("ad.list.retry"),
                        emptyTitle: intl.formatMessage("ad.list.empty_title"),
                        emptyDescription: intl.formatMessage("ad.list.empty_description"),
                        resetFilters: intl.formatMessage("ad.list.reset_filters"),
                        ...(isLoggedIn ? { endOfList: intl.formatMessage("ad.list.end_of_list") } : {}),
                    }}
                >
                    {(items: AdInterface[]) => <ComponentList items={items} />}
                </InfiniteListArea>
            </div>
        </React.Fragment>
    );
};

const Wrapper: React.FC = () => {
    const intl = useContext(IntlContext);

    return (
        <InfiniteListPage
            title={String(intl.formatMessage("ad.list.title_prefix"))}
            titleHighlight={String(intl.formatMessage("ad.list.title_highlight"))}
            subtitle={String(intl.formatMessage("ad.list.subtitle"))}
            dataFetcher={fetchOurAds}
            defaultFetcherParams={{ ...DEFAULT_FETCHER_PARAMS }}
            cacheKey="ads-list"
            className="bg-slate-50"
        >
            <AdsList />
        </InfiniteListPage>
    );
};

export default Wrapper;
